import React, { useEffect, useState } from 'react';
import { RadioButton, StatusMessage } from 'is24-corecss';
import { useTranslation } from 'react-i18next';
import { IconS24Close24 } from '@is24/cosma-ui-icons';
import IdentificationInfoContent from '../../components/identificationInfoContent/IdentificationInfoContent';
import './VerificationLandingPage.css';
import VeriffVerification from '../../components/veriffVerification/VeriffVerification';
import { VerificationMethod } from '../../@types/VerificationMethod';
import { useTvsState } from '../../utils/useTvsState';
import { getUserVerified } from '../../api/tvs/userVerifications';
import { VerificationStatus } from '../../@types/VerificationStatus';
import { trackPageView } from '../../tracking/tracking';
import { useClientConfig } from '../../utils/clientConfigContext';
import { ApiResponseError } from '../../api/ApiResponseError';
import WebIdVerification from '../../components/webIdVerification/WebIdVerification';
import InfoPopOver from '../../components/infoPopOver/InfoPopOver';
import veriffLogo from '../../components/veriffVerification/veriff.svg';
import webIdLogo from '../../components/webIdVerification/webid-logo.svg';

interface VerificationLandingPageProps {
  shouldShowErrorMessage: boolean;
  setWebIdPhoneNumber?: (phoneNumber: string | null) => void;
  onVerificationComplete?: (status: VerificationStatus) => void;
}

const VerificationLandingPage: React.FC<VerificationLandingPageProps> = ({
  shouldShowErrorMessage,
  setWebIdPhoneNumber = () => {},
  onVerificationComplete,
}) => {
  const { t } = useTranslation();
  const [method, setMethod] = useState<VerificationMethod>(VerificationMethod.Veriff);
  const [modalContent, setModalContent] = useState<React.ReactNode>(null);
  const [, setTvsState] = useTvsState();
  const [shouldShowPage, setShouldShowPage] = useState(shouldShowErrorMessage);
  const [errorMessage, setErrorMessage] = useState('');
  const { clientId } = useClientConfig();
  const isDarkModeOn = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;

  const checkVerificationStatus = () => {
    getUserVerified()
      .then((response) => {
        if (response?.isVerified === true) {
          setTvsState('verification_success');
          onVerificationComplete?.(VerificationStatus.SUCCESS);
        } else {
          setShouldShowPage(true);
        }
      })
      .catch((error: ApiResponseError) => {
        if (error.responseStatus === 401) {
          setErrorMessage(t('errors.unauthorized'));
        }
        setShouldShowPage(true);
      });
  };

  const handleErrorsFromVerificationMethod = (errorMessage: string) => {
    setErrorMessage(t(errorMessage as any));
  };

  useEffect(() => {
    if (!shouldShowErrorMessage) {
      checkVerificationStatus();
      trackPageView('is24.de.sicherheit.verifizierung.modal', clientId);
    } else {
      setErrorMessage(t('VerificationLandingPage.errorMessage'));
    }
  }, []);

  return (
    <>
      {shouldShowPage && (
        <div aria-modal='true' className='modal-backdrop' style={{ zIndex: 2000 }}>
          <div className='modal-container' style={{ width: 601 }}>
            <div className='modal-title-wrapper'>
              <h4 className='modal-headline'>{t('VerificationLandingPage.heading')}</h4>
              <IconS24Close24
                onClick={() => setTvsState('')}
                data-testid='modal-close-icon'
                className='tvs-close-button'
              />
            </div>
            <div className='modal-content-wrapper display-flex'>
              <div className='modal-content palm-display-flex'>
                {errorMessage && (
                  <StatusMessage
                    data-testid='error-message'
                    className='margin-bottom-xxl tvs-status-message'
                    headline={t('VerificationLandingPage.errorHeading')}
                    message={errorMessage}
                    statusType='error'
                  />
                )}
                <div className='grid grid-flex margin-bottom-m'>
                  <IdentificationInfoContent
                    informationText='VerificationLandingPage.content'
                    isDarkModeOn={isDarkModeOn}
                  />
                </div>
                <div className='grid-item font-heading-xsmall-bold margin-top-m palm-margin-bottom-none'>
                  {t('VerificationLandingPage.availableOptions')}
                </div>
                <div className='grid grid-flex margin-top-xxl gutter-horizontal-m margin-top-m palm-margin-bottom-m palm-margin-top-m'>
                  <div className='grid-item one-whole font-h5'>
                    <RadioButton
                      label={
                        <div className='grid grid-flex'>
                          {t('VerificationLandingPage.VeriffRadioButtonLabel')}
                          <div className='font-lightgray font-body-small-light margin-bottom-s'>
                            {t('VerificationLandingPage.VariffBracketName')}
                          </div>
                          <div className='grid grid-flex grid-align-center'>
                            <p className='margin-right-xs font-lightgray font-body-small-regular'>
                              {t('VerificationLandingPage.footerText')}
                            </p>
                            <img
                              src={veriffLogo}
                              alt='verification'
                              className='footer-icon'
                              height={16}
                              width={49.48}
                            />
                            <InfoPopOver content={t('InformationText.veriffPopOverContent', { returnObjects: true })} />
                          </div>
                        </div>
                      }
                      id={VerificationMethod.Veriff}
                      name='verification-method'
                      checked={method === VerificationMethod.Veriff}
                      onChange={(event) => setMethod(event.target.id as VerificationMethod)}
                    />
                  </div>
                  <div className='grid-item one-whole font-h5 grid-justify-center margin-top-s'>
                    <RadioButton
                      label={
                        <div className='grid grid-flex'>
                          {t('VerificationLandingPage.WebIdRadioButtonLabel')}
                          <div className='font-lightgray font-body-small-light margin-bottom-s'>
                            {t('VerificationLandingPage.WebIDBracketName')}
                          </div>
                          <div className='grid grid-flex grid-align-center palm-padding-bottom-l'>
                            <p className='margin-right-xs font-body-small-regular font-lightgray'>
                              {t('VerificationLandingPage.footerText')}
                            </p>
                            <img src={webIdLogo} alt='verification' className='footer-icon' height={13} width={49.48} />
                            <InfoPopOver
                              content={t('InformationText.webIdPopOverContent' as any, { returnObjects: true })}
                            />
                          </div>
                        </div>
                      }
                      id={VerificationMethod.WebId}
                      name='verification-method'
                      checked={method === VerificationMethod.WebId}
                      onChange={(event) => setMethod(event.target.id as VerificationMethod)}
                    />
                  </div>
                </div>
                {modalContent}
              </div>
            </div>
            {method === VerificationMethod.Veriff ? (
              <VeriffVerification
                onContentChange={setModalContent}
                onError={(errorMessage) => handleErrorsFromVerificationMethod(errorMessage)}
              />
            ) : (
              <WebIdVerification
                onContentChange={setModalContent}
                onError={(errorMessage) => handleErrorsFromVerificationMethod(errorMessage)}
                setPhoneNumberForTan={setWebIdPhoneNumber}
              />
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default VerificationLandingPage;
